/**
 * TransportPicker — the canonical 3-card transport chooser
 * used at every passkey decision point in PureID:
 *
 *   - admin invite enrollment wizard (setup-time choice)
 *   - portal JIT enrollment (setup-time choice)
 *   - "add a passkey" dialog (setup-time choice)
 *   - sign-in fallback after WebAuthn cancel (re-pick transport)
 *   - /login/help page (always-reachable assistance)
 *
 * One vocabulary across enrollment AND sign-in fallback —
 * users learn "phone / this device / security key" once.
 *
 * Capability-aware: when /identify supplies the user's
 * deduplicated transport tag list (KnownUserResult.
 * available_transports), the picker disables cards that have
 * no chance of working and explains why ("no passkey for this
 * browser") instead of letting the user click through to an
 * empty WebAuthn dialog. When the list is missing or empty
 * (legacy rows / enrollment-time / pre-M2 backend), every
 * card is enabled — the most permissive behaviour.
 *
 * Optional escape hatches below the cards:
 *   - "Use a different account" — back to email entry
 *   - "Lost your passkey?" — recovery flow
 * Both are off by default; consumers opt in. Recovery is
 * deliberately demoted (link styling, separator above,
 * "lost everything" qualifier) so it's reachable but never
 * the obvious answer.
 */

import * as React from "react";
import {
  ArrowRight,
  ChevronDown,
  Info,
  KeyRound,
  Laptop,
  RotateCcw,
  ListChecks,
  Smartphone,
  Usb,
} from "lucide-react";
import type { EnrollmentChoice, PasskeyTransport, UaHint } from "@pureid/auth-core";
import { AccountRecoveryOption } from "./account-recovery-option";
import {
  isMobileBrowser,
  isPasskeyTransport,
  isSelectableCard,
  isTransportAvailable,
  probeHybridFeasibility,
  probePlatformAuthenticator,
} from "@pureid/auth-core";
import { Button } from "../button";
import { cn } from "../../lib/utils";
import { TransportIllustration } from "./transport-illustration";
import {
  NoWorkingMethodScreen,
  type NoWorkingMethodCause,
} from "./no-working-method-screen";
import {
  PasskeyLivesAnimation,
  type AuthenticatorFamily,
} from "./passkey-lives-animation";

export interface TransportPickerProps {
  /** Currently-selected transport. The picker is fully
   *  controlled — the consumer holds the state and re-renders
   *  on every change. Combine with the `useCeremonyTransport`
   *  hook from `@pureid/ui/hooks/use-ceremony-transport` for a
   *  sane initial value that layers
   *  `localStorage → server pref → server recommendation →
   *  product default` behind one binding.
   *
   *  Type is PasskeyTransport (3 concrete options) — the
   *  broader "any" preference exists at the retry-runner
   *  level (HintPreference) but the picker UI never renders
   *  it as a card. */
  value: EnrollmentChoice;
  /** Card click handler — fires before commit so the consumer
   *  can persist intermediate selection (e.g. localStorage). */
  onChange: (next: EnrollmentChoice) => void;
  /** Continue button handler. Receives the currently-selected
   *  value. The consumer MUST narrow it once: route `pureid_qr`
   *  to the QR flow, pass the remaining PasskeyTransport to the
   *  local ceremony. (Sign-in mode never emits `pureid_qr`, but
   *  the type is shared so sign-in callers narrow defensively.) */
  onContinue: (value: EnrollmentChoice) => void;

  /** Deduped transport tags from /identify
   *  (KnownUserResult.available_transports). Drives the
   *  capability-aware disabling. Undefined / empty → all
   *  cards enabled (permissive default for enrollment +
   *  legacy rows). */
  availableTransports?: readonly string[];

  /** When true AND onChangeAccount is set, render a small
   *  "Use a different account" link below the cards. */
  showAccountSwitch?: boolean;
  onChangeAccount?: () => void;

  /** When true AND onStartRecovery is set, render the
   *  recovery escape hatch below the cards (link styling,
   *  separator above, demoted relative to transport
   *  switching). */
  showRecovery?: boolean;
  onStartRecovery?: () => void;

  /** Handler that navigates to the tenant's PureID QR-channel
   *  sign-in surface (typically `/signin-qr`). When supplied
   *  AND tenantQrSigninEnabled is true, the dead-end
   *  `NoWorkingMethodScreen` renders a primary "Sign in with
   *  your phone (no Bluetooth needed)" CTA — the headline
   *  recovery path for caBLE-hostile users. Distinct from
   *  `onStartRecovery` (which is the lost-passkey flow). */
  onUseQrSignin?: () => void;

  /** Phase G — enrollment-mode counterpart to onUseQrSignin.
   *  When wired AND tenantQrSigninEnabled is true AND
   *  mode === "enrollment", the dead-end NoWorkingMethodScreen
   *  surfaces a "Save your passkey via QR" CTA that routes to
   *  /enroll-qr (Card 2 phone-side ceremony). */
  onUseQrEnroll?: () => void;

  /** Header copy. Defaults: "How would you like to sign in?"
   *  / a one-line subtitle. Override at enrollment time
   *  ("How would you like to register?") or on the help page
   *  ("Choose a different way to sign in"). */
  title?: string;
  subtitle?: string;

  /** Continue button label. Default "Continue". */
  submitLabel?: string;

  /** Disable cards + button (e.g. while a ceremony kick-off
   *  is pending). */
  disabled?: boolean;

  /** Optional diagnostic banner rendered above the cards.
   *  Used by sign-in surfaces to NAME the situation the user
   *  is in before they pick (e.g. "Looks like your passkey
   *  lives on another device") so the cards aren't read as
   *  free-form options but as a triage menu. Pure
   *  presentation — caller decides when to show it. */
  diagnostic?: React.ReactNode;

  /** Optional per-card "what will happen next" preview
   *  copy. When provided, replaces the option's default
   *  subtitle on AVAILABLE cards (disabled cards still
   *  show their unavailability reason). Used by the cross-
   *  device-fallback scenario to set concrete expectations
   *  per option ("we'll show a QR code; scan with your
   *  phone's camera"). */
  cardPreviewCopy?: Partial<Record<PasskeyTransport, string>>;

  /** Tenant attachment policy from the /identify response
   *  (Item 5). Drives the "no working method" cause
   *  classification — when the picker has zero usable cards
   *  AND the tenant policy excludes everything the device
   *  can do, the cause is "policy_excludes_capable" rather
   *  than "no_platform_no_hybrid". Without this signal we
   *  fall back to the device-side cause label. */
  tenantAttachmentPolicy?: string;

  /** Tenant's WebAuthn-hybrid (caBLE / OS-passkey-picker
   *  "Use another device") kill-switch. When TRUE, the
   *  `other_device` card is rendered as policy-disabled with
   *  admin-direction copy AND the empty-intersection check
   *  fires correctly when hybrid was the only capable
   *  channel. Treated as FALSE (hybrid allowed) when
   *  undefined — the canonical safe default. */
  tenantWebAuthnHybridDisabled?: boolean;

  /** Tenant's PureID QR-channel kill-switch (parallel to
   *  tenantWebAuthnHybridDisabled). Lets the disabled-card
   *  copy + NoWorkingMethodScreen point users at the QR
   *  fallback when it's live, or degrade gracefully when
   *  it's off. Treated as TRUE when undefined — the canonical
   *  default. */
  tenantQrSigninEnabled?: boolean;

  /** UA hint passed through to the NoWorkingMethodScreen so
   *  the deep-link helper can resolve the right "Set up
   *  Windows Hello / Touch ID" target. */
  ua?: UaHint;

  /** Tenant-configured support email passed through to the
   *  NoWorkingMethodScreen for the "Contact your administrator"
   *  link. */
  supportEmail?: string;

  /** Re-probe callback wired by the consumer. When the
   *  NoWorkingMethodScreen renders and the user clicks
   *  "I've set it up — try again", the picker invalidates
   *  the probe cache and invokes this so the parent surface
   *  re-renders with fresh capability state. */
  onRetry?: () => void;

  /** Mode passed to NoWorkingMethodScreen to choose between
   *  sign-in copy and enrollment copy. Defaults to "signin". */
  mode?: "signin" | "enrollment";

  /** Phase G P5 — pre-Continue card-click analytics hook. Fires on
   *  every card click (the same moment `onChange` fires) so
   *  consumers can emit a `picker_card_clicked` telemetry event
   *  without re-implementing card-click handling. Distinguishes
   *  "user explored card X" (this hook) from "user committed to
   *  card X" (the existing reportCeremonyEvent at phase=begin).
   *  Both signals are needed to distinguish abandoned vs.
   *  committed cards in funnel dashboards. Omitting this prop
   *  is a no-op (no analytics emitted). */
  onCardClicked?: (transport: EnrollmentChoice) => void;

  /** Phase G P6 — tenant microcopy override for the pureid_qr
   *  enrollment card title. Empty string or undefined → fall back
   *  to the canonical "Save on my phone" default. Consumers wire
   *  this from the tenant's microcopy.pureid_qr_card_title slot
   *  (see backend `internal/tenant/microcopy`). Frontline tenants
   *  commonly set this to "Use site badge" or similar. Length is
   *  enforced server-side (60 chars max) — the picker trusts
   *  the value as-is. */
  pureidQRCardTitleOverride?: string;

  /** Phase 4 (2026-05-25) — card-impression telemetry hook.
   *  Fired ONCE on mount with the list of cards actually
   *  rendered AND the list of cards suppressed (pureid_qr
   *  case carries a typed reason from PureIDQRSuppressionReason).
   *. The consumer wires this to reportCeremonyEvent so dashboards
   *  can track the impression → click → completion funnel and
   *  alarm on regressions (pureid_qr impression-rate drops > 50%
   *  week-over-week when the policy is enabled).
   *
   *  Distinct from onCardClicked (which is per-click). Together
   *  they bound the funnel: impressions, clicks, completions
   *  (via ceremony begin/finish events). */
  onPickerImpression?: (info: PickerImpression) => void;

  /** Phase 4 (2026-05-25) / I9 — programmer-misconfiguration
   *  telemetry hook. Fired when the picker is mounted in
   *  `mode="enrollment"` with policy+UA both saying Card 2
   *  should be visible but `onUseQrEnroll` was not wired. This
   *  is the production-side signal for the dev-time console.error
   *  the I9 runtime check emits. Consumers wire this to a
   *  dedicated telemetry event ("picker_misconfigured") so SOC
   *  can detect a regression that ships past code review.
   *  Phase 5 alarm consumes this event. */
  onPickerMisconfigured?: () => void;

  /** The tenant's enrollment-card allowlist —
   *  `tenants.enrollment_transports`, as projected on
   *  /api/loginflow/v1/config. Cards outside it are not rendered.
   *
   *  ENROLLMENT MODE ONLY, and that is load-bearing rather than
   *  incidental: this column curates which cards a tenant OFFERS
   *  when a credential is being created. Applying it at sign-in
   *  would hide the card a user needs to present a passkey they
   *  already hold — an admin narrowing the enrollment catalogue
   *  would lock existing users out of their own account, which is
   *  not a policy anyone asked for.
   *
   *  ABSENT means "no policy supplied", and every card renders.
   *  That is the FAIL-OPEN direction on purpose: a surface that has
   *  not adopted the policy, or whose config read failed, must not
   *  leave a person unable to register any passkey at all. The one
   *  card that fails CLOSED is `any` (see allowAnyOption and
   *  offersAnyPasskeyOption) — it widens what the OS prompt offers,
   *  so an unconfirmed answer resolves to off. Availability fails
   *  open; anything that widens fails closed.
   *
   *  Hidden, not disabled-with-a-reason: the attachment-policy
   *  props below disable a card because a CONSTRAINT would make its
   *  ceremony fail, and naming the constraint helps. This is
   *  curation — the tenant chose not to offer it — and a greyed row
   *  explaining an absence the admin intended is noise. */
  allowedCards?: readonly string[];

  /** Offer the "no preference" card (`any`) in enrollment mode.
   *
   *  Opt-in, and deliberately not on by default: the three-card
   *  enrollment set is a locked design shared by admin invite
   *  enrollment, portal JIT enrollment and recovery re-enrollment,
   *  where the whole point is to steer a first-time user to ONE
   *  destination. This card is for the surface where the user
   *  already has a passkey and is adding another on purpose —
   *  they know what they want, and the picker's per-card
   *  capability probes are exactly the thing that can be wrong
   *  about it (a Chromium build whose platform probe says no, a
   *  key the tenant allows but this account has never registered).
   *  Picking it sends NO hint, so the browser's own prompt offers
   *  everything the tenant's authenticatorSelection permits.
   *
   *  Sign-in mode ignores this: `hint` on an ASSERTION is a
   *  different question (it filters allowCredentials, not the
   *  creation target), and nothing has asked for it there. */
  allowAnyOption?: boolean;
}

export interface PickerImpression {
  /** Cards rendered as ENABLED, in display order. */
  shown: ReadonlyArray<EnrollmentChoice>;
  /** Cards rendered as DISABLED (the F5 user-visible
   *  misconfiguration / policy-disabled state) along with the
   *  reason. Currently only pureid_qr surfaces here; future
   *  reasons (e.g., this_device probe-false) would extend. */
  suppressed: ReadonlyArray<{
    value: EnrollmentChoice;
    reason: PureIDQRSuppressionReason | "other";
  }>;
  /** Picker mode at impression time. */
  mode: "signin" | "enrollment";
}

interface TransportOption {
  value: EnrollmentChoice;
  title: string;
  /** The single always-visible description: leads with WHEN to pick
   *  this option, then a brief how. Rendered in full on every card
   *  (no expand/collapse) — keep to ~1–2 short sentences. */
  subtitle: string;
  icon: React.ReactNode;
  /** When the option is unreachable for the user's stored
   *  transports, this short copy explains WHY the card is
   *  disabled. The picker substitutes it for `subtitle` on a
   *  disabled card. */
  unavailableReason: string;
}


const PHONE_OPTION: TransportOption = {
  value: "other_device",
  // Sign-in copy. The mechanism beneath is still `other_device`
  // (OS hybrid OR Phase F /signin-qr per tenant policy).
  title: "Use another device",
  subtitle:
    "Recommended when this computer isn't yours — a shared, remote, or virtual machine. Scan a QR code and approve on your phone; your passkey stays on your phone and is never copied here.",
  icon: <Smartphone className="h-5 w-5" />,
  unavailableReason:
    "Your passkey can't sign in cross-device — it's tied to a fixed authenticator.",
};

const THIS_DEVICE_OPTION: TransportOption = {
  value: "this_device",
  title: "Use this device",
  subtitle:
    "Recommended when this is your own computer — your regular work machine or a trusted device. You may be asked to use Windows Hello, a password manager, or another passkey provider you've set up.",
  icon: <Laptop className="h-5 w-5" />,
  unavailableReason:
    "No passkey for this browser. Sign in via your phone or security key, then enroll a local passkey.",
};

const SECURITY_KEY_OPTION: TransportOption = {
  value: "security_key",
  title: "Use a security key or smart card",
  subtitle:
    "Recommended when your company has issued you a YubiKey, smart card, or badge. Plug in, tap, or insert it when prompted.",
  icon: <Usb className="h-5 w-5" />,
  unavailableReason: "No security key registered for this account.",
};

// Phase G — PureID-QR enrollment-only card. Replaces PHONE_OPTION
// in the enrollment-mode option set when the tenant has the
// channel enabled AND the user is on a non-mobile browser.
// Distinct semantics from PHONE_OPTION:
//   - Routes to /enroll-qr (PureID's own QR ceremony, no
//     OS-mediated hybrid).
//   - Phone runs WebAuthn.create() locally against the tenant
//     RP ID — no caBLE pairing, no Bluetooth radio required.
//   - Sign-in surfaces continue to use PHONE_OPTION
//     (other_device → hybrid OR Phase F signin-qr).
//
// 2026-05-25 UX F1 — distinct title. The sign-in PHONE_OPTION
// is titled "Use another device" (because at sign-in time the
// user doesn't yet know whether the mechanism will be caBLE-
// hybrid or PureID-QR; "another device" is honest ambiguity).
// At ENROLLMENT time the user IS deciding where to save the
// new passkey, and the destination is concrete: their phone.
// Using "Use another device" on both surfaces caused the F1
// cross-mode confusion documented in the strategy doc — a
// user who enrolled here saw a same-named sign-in card next
// time and clicked it, sometimes landing on a different
// mechanism that failed in their environment. The action-led
// "Save your passkey on a phone" matches Cards 1/3's
// destination-naming structure and removes the collision.
const PUREID_QR_OPTION: TransportOption = {
  value: "pureid_qr",
  title: "Save your passkey on a phone",
  subtitle:
    "For shared or remote computers. Scan a QR code to save the passkey on your phone.",
  icon: <Smartphone className="h-5 w-5" />,
  // Card now CAN render disabled — see enrollmentOptionsWithReasons.
  // The unavailable copy below is the catch-all when no more
  // specific reason is supplied; reasonFor() returns a more
  // targeted message for each suppression cause.
  unavailableReason:
    "Phone setup isn't available right now. Use this device or a security key instead.",
};

// Phase G enrollment-mode option copy. Overrides the sign-in
// constants above when mode === "enrollment". The sign-in
// constants remain authoritative for the sign-in path so
// returning-user vocabulary doesn't drift.
const THIS_DEVICE_ENROLLMENT_OPTION: TransportOption = {
  value: "this_device",
  // The OS picker handoff. Title is the device-aware "Use this
  // <device>" (set at render by deviceAwareThisDevice from the UA —
  // a fact we can read); we do NOT name the credential provider (we
  // can't know whether the user defaults to Windows Hello, a
  // password manager, etc.). We do not force
  // authenticatorAttachment=platform (see registrationAttachmentMode
  // in ceremony.go).
  title: "Use this device",
  subtitle:
    "For your own trusted device. Create a passkey with your fingerprint, face, or screen lock.",
  icon: <Laptop className="h-5 w-5" />,
  unavailableReason:
    "Your tenant's policy excludes the OS-picker enrollment path. Pick another option.",
};

// The "no preference" card. Opt-in per surface (allowAnyOption) —
// see the prop docs for why it is not in the locked three-card set.
//
// Mechanically it is the ABSENCE of a hint: `any` is a member of the
// backend's KnownUIPreferences and HintsFromUIPreference maps it to
// nil, which registrationAttachmentMode reads as "honor the tenant's
// configured authenticatorSelection verbatim". So this card is the
// only one that adds no bias of its own — pinned byte-identical to
// sending no hint at all by TestNoPreferenceCardsAreIdentical.
//
// The subtitle says where the choice happens, because that is the
// one thing the user needs to know: the decision does not disappear,
// it moves to the browser's own prompt. It does not enumerate the
// types — the prompt shows what the tenant actually allows, and a
// card that lists three options the tenant may have narrowed to one
// would be the picker over-claiming again.
const ANY_OPTION: TransportOption = {
  value: "any",
  title: "Show all options",
  subtitle:
    "You can pick the type on the OS prompt. Choose this if you know what you want to use, or if the options above don't look right for this device.",
  icon: <ListChecks className="h-5 w-5" />,
  // Never rendered: this card asserts nothing about the device, so
  // no probe and no stored-credential check can disqualify it (see
  // deviceCapable). Present because TransportOption requires it.
  unavailableReason:
    "Your tenant's policy leaves no authenticator this browser can use.",
};

const SECURITY_KEY_ENROLLMENT_OPTION: TransportOption = {
  value: "security_key",
  title: "Use a security key or smart card",
  subtitle:
    "For a compatible security key, smart card, or badge. Connect it to create your passkey.",
  icon: <KeyRound className="h-5 w-5" />,
  unavailableReason: "No security key registered for this account.",
};

// deviceAwareThisDevice names the DEVICE in the "Use this device"
// title — a fact we can read from the UA — never the credential
// provider, which we cannot know (a Windows user may default to a
// password manager, not Windows Hello). Title only; the card's
// description stays generic. Falls back to the base "Use this device"
// on unknown OS / Linux.
function deviceAwareThisDevice(
  base: TransportOption,
  ua: UaHint | undefined,
): TransportOption {
  switch (ua?.os) {
    case "Windows":
      return { ...base, title: "Use this Windows PC" };
    case "macOS":
      return { ...base, title: "Use this Mac" };
    case "iOS":
      return { ...base, title: "Use this iPhone or iPad" };
    case "Android":
      return { ...base, title: "Use this Android device" };
    default:
      return base;
  }
}

// Sign-in render set — Card 1 is device-aware (its title names the
// user's device); cards 2–3 are static. Mirrors enrollmentOptions.
// Which member of `enrollment_transports` governs a given card.
//
// The two vocabularies are NOT the same list, and assuming they were
// is how this went wrong twice. The column names the paths a tenant
// curates; the picker names the cards it renders, and Phase G
// changed the mechanism behind one of them without renaming the
// policy:
//
//   this_device  → the OS-picker card, same name
//   security_key → the roaming-key card, same name
//   any          → the no-preference card, same name
//   pureid_qr    → THE PHONE CARD, curated as `other_device`
//
// That last line is the whole reason this function exists. The
// enrollment phone card is `pureid_qr` (PureID's own QR channel),
// while the allowlist's name for "the phone path" is still
// `other_device` from when the mechanism was OS hybrid. Filtering
// `pureid_qr` by its own name hides it for every tenant, because no
// tenant's allowlist can contain it — and exempting it instead
// means an admin who unticks the phone option watches the phone
// card stay put, which is a deny that does not deny. In a security
// control that is the worse of the two failures.
//
// The channel kill-switch (tenantQrSigninEnabled) still applies on
// top: the allowlist says whether the phone path is OFFERED, the
// switch says whether PureID's QR channel is the live mechanism for
// it. Both must pass.
//
// `other_device` itself only appears in the sign-in option set,
// which this filter never touches.
function allowlistMemberFor(card: EnrollmentChoice): string {
  switch (card) {
    case "pureid_qr":
      return "other_device";
    case "this_device":
    case "other_device":
    case "security_key":
    case "any":
      return card;
  }
}

function signinOptions(ua: UaHint | undefined): TransportOption[] {
  return [
    deviceAwareThisDevice(THIS_DEVICE_OPTION, ua),
    PHONE_OPTION,
    SECURITY_KEY_OPTION,
  ];
}

// Enrollment options — Phase G locked design with 2026-05-25
// (Phase G+) I9 hardening. Card 2 (PUREID_QR_OPTION) is gated on:
//
//   1. tenant channel enabled (kill switch, server-side policy)
//      — drives `kind: "enrollment-no-qr", reason: "tenant_policy_disabled"`
//      semantics at the consumer level (today expressed via
//      tenantQrSigninEnabled=false; see the I9 deferred
//      compile-time enforcement below).
//   2. non-mobile (mobile-on-mobile is a category error — user
//      is already on a passkey-capable device).
//   3. consumer wired the navigation handler (`consumerCanRoute`).
//
// I9 enforcement (2026-05-25): when (1) and (2) pass BUT (3)
// fails, the picker no longer silently hides the card —
// `renderConfigurationErrorCard` below surfaces a developer-
// visible misconfiguration card and emits a console.error at
// dev time. This is the "explicit failure mode" the strategy
// doc §4.2 commits to; the full discriminated-union compile-
// time enforcement is deferred to a follow-up (the structural
// shape requires touching PasskeyCeremony + TransportChooser +
// every consumer in one PR).
//
// In production every reachable JIT-enrollment surface MUST
// wire `onUseQrEnroll`. The login-help pages and signin-cancelled
// surfaces deliberately mount in mode="signin" so this branch
// doesn't apply to them.
// 2026-05-25 (UX F5) — reason vocabulary for a suppressed
// pureid-QR card. Drives both the rendered disabled-card copy
// (so the user sees WHY the card isn't selectable rather than
// silently missing) AND the picker_cards_shown telemetry
// signal (so SOC can detect a regression).
export type PureIDQRSuppressionReason =
  | "tenant_policy_disabled" // tenant flipped pureid_qr_channel_enabled=false
  | "mobile_browser" // user is on a phone — finish here, no QR-to-self
  | "consumer_not_wired"; // I9 — consumer surface didn't pass onUseQrEnroll

interface EnrollmentCardPlan {
  options: TransportOption[];
  pureidQRReason: PureIDQRSuppressionReason | null;
}

function enrollmentOptions(
  channelEnabled: boolean,
  mobile: boolean,
  consumerCanRoute: boolean,
  ua: UaHint | undefined,
  allowAny: boolean,
): EnrollmentCardPlan {
  const cards: TransportOption[] = [];
  cards.push(deviceAwareThisDevice(THIS_DEVICE_ENROLLMENT_OPTION, ua));

  // F5 — Card 2 is rendered in all three states:
  //   1. enabled (channel allowed + non-mobile + consumer wired)
  //   2. disabled with reason (mobile, OR consumer didn't wire,
  //      OR tenant policy off)
  // The strategy doc §9 acceptance criterion requires
  // distinguishable copy per reason; we surface that via
  // reasonFor() at the consumer level using pureidQRReason
  // and unavailableReason on the card option.
  let reason: PureIDQRSuppressionReason | null = null;
  if (!channelEnabled) {
    reason = "tenant_policy_disabled";
  } else if (mobile) {
    reason = "mobile_browser";
  } else if (!consumerCanRoute) {
    // I9 — consumer programming error. Still surfaces a card so
    // the user has visible evidence something was meant to be
    // here (the dev-time console.error fires too).
    reason = "consumer_not_wired";
  }
  // For mobile_browser we still HIDE the card (the user is on
  // their phone; "save on a phone" reading on the phone is a
  // category error). For the other two reasons we render it
  // disabled so the user sees the explanation.
  if (reason === null) {
    cards.push(PUREID_QR_OPTION);
  } else if (reason !== "mobile_browser") {
    cards.push(PUREID_QR_OPTION);
  }

  cards.push(SECURITY_KEY_ENROLLMENT_OPTION);
  // LAST, always. The first card is the recommendation and the
  // ordering above is the steer; an escape hatch that outranks the
  // thing it is an escape from is not an escape hatch.
  if (allowAny) {
    cards.push(ANY_OPTION);
  }
  return { options: cards, pureidQRReason: reason };
}

// I9 enforcement helper (2026-05-25). Reports the missing-
// handler misconfiguration to the developer console at mount
// time. Idempotent across renders (the closure captures the
// "already reported" state via the React effect).
function reportMisconfiguredEnrollmentPicker(
  channelEnabled: boolean,
  mobile: boolean,
  consumerCanRoute: boolean,
): void {
  if (channelEnabled && !mobile && !consumerCanRoute) {
    console.error(
      "[TransportPicker] I9 violation: mounted in enrollment mode with " +
        "tenantQrSigninEnabled=true and a non-mobile UA, but " +
        "onUseQrEnroll was NOT wired by the consumer. The pureid-QR " +
        "Card 2 is therefore unreachable on this surface. This is a " +
        "PROGRAMMING ERROR (not a tenant-policy decision); fix by " +
        "wiring onUseQrEnroll, OR if Card 2 is intentionally hidden " +
        "set tenantQrSigninEnabled=false explicitly. See " +
        "docs/product-areas.md",
    );
  }
}

/**
 * Map the (selected transport, UA hint) pair to the authenticator
 * family the `<PasskeyLivesAnimation>` accepts. Used at enrollment
 * time only — the animation teaches the mental model "where your
 * passkey will be saved" before the OS dialog fires.
 *
 * The mapping is heuristic: we know what's likely from UA hints,
 * not what's certain. For `this_device` we infer the platform
 * authenticator from the OS family (Apple → iCloud Keychain,
 * Android → GPM, Windows → Windows Hello). For `other_device` we
 * use `generic-platform` because the destination lives on the
 * user's phone, which we have no UA hint for. For `security_key`
 * we use `generic-roaming` — the user might plug in a YubiKey,
 * Titan, Feitian, etc., and the animation copy stays accurate
 * across all of them.
 *
 * Returns null when we have no opinion (no UA hint AND not enough
 * signal from the transport), in which case the caller should
 * suppress the animation rather than show a misleading default.
 */
function authenticatorFamilyFor(
  transport: PasskeyTransport,
  ua: UaHint | undefined,
): AuthenticatorFamily | null {
  if (transport === "security_key") return "generic-roaming";
  if (transport === "other_device") return "generic-platform";
  // this_device: infer from OS family.
  const os = ua?.os?.toLowerCase() ?? "";
  if (os.includes("ios") || os.includes("ipad") || os.includes("mac")) {
    return "apple-keychain";
  }
  if (os.includes("android")) return "google-password-manager";
  if (os.includes("windows")) return "windows-hello";
  if (os.includes("chrome os") || os.includes("cros")) return "google-password-manager";
  // Linux + unknown OS → no opinion. Generic-platform would be
  // misleading because Linux has no canonical synced-passkey
  // provider — implying one to a Linux user is worse than
  // showing no animation. Empty UA hint lands here too.
  return null;
}

export function TransportPicker({
  value,
  onChange,
  onContinue,
  availableTransports,
  showAccountSwitch,
  onChangeAccount,
  showRecovery,
  onStartRecovery,
  title = "How would you like to sign in?",
  subtitle = "Pick the device that has your passkey. We'll remember your choice for next time.",
  submitLabel = "Continue",
  disabled,
  diagnostic,
  cardPreviewCopy,
  tenantAttachmentPolicy,
  tenantWebAuthnHybridDisabled,
  tenantQrSigninEnabled,
  onUseQrSignin,
  onUseQrEnroll,
  ua,
  supportEmail,
  onRetry,
  mode = "signin",
  onCardClicked,
  pureidQRCardTitleOverride,
  onPickerImpression,
  onPickerMisconfigured,
  allowAnyOption = false,
  allowedCards,
}: TransportPickerProps) {
  // Resolve the QR-channel flag with the canonical default
  // semantics (undefined → TRUE). Lifted to the top so every
  // copy branch can consult it without re-deriving.
  const qrChannelLive = tenantQrSigninEnabled !== false;

  // Phase G — mode-aware option set. Sign-in mode keeps the
  // historical 3 cards (other_device for the cross-device
  // phone path). Enrollment mode swaps PHONE_OPTION for
  // PUREID_QR_OPTION when the tenant channel is live and the
  // user is on a non-mobile browser; otherwise the phone
  // card is omitted entirely (per locked design: hybrid is
  // reachable via Card 1's OS picker, not a dedicated card).
  // Mobile mobile-on-mobile suppression matches the "you
  // don't QR-enroll yourself" rule.
  const mobile = isMobileBrowser();
  const consumerCanRoute = typeof onUseQrEnroll === "function";
  // Phase 4 (2026-05-25) — fire the picker-impression event
  // ONCE on mount with the computed shown/suppressed lists. The
  // effect is keyed on the option-set and reason fingerprint
  // so a real re-mount (e.g., user navigates away and back)
  // re-emits but a parent re-render does not. Consumer-side
  // dedup is the consumer's responsibility.
  React.useEffect(() => {
    if (!onPickerImpression) return;
    // Cards in the rendered set that passed the availability
    // probe; everything in the picker.
    const shown: EnrollmentChoice[] = [];
    const suppressed: { value: EnrollmentChoice; reason: PureIDQRSuppressionReason | "other" }[] = [];
    // Iterate effectiveOptions to catch the rendered-but-
    // disabled (pureid_qr policy-disabled / consumer-not-wired)
    // case. The mobile_browser case drops Card 2 from the
    // array entirely, so it appears in suppressed[] without
    // being in shown[].
    // (Compute lazily below to avoid a hoist cycle with
    // effectiveOptions / pureidQRReason which are declared
    // further down — the effect runs after render commits,
    // so the values are stable in closure.)
     
    for (const opt of effectiveOptions) {
       
      if (choiceAvailable(opt.value)) {
        shown.push(opt.value);
      } else if (opt.value === "pureid_qr" && pureidQRReason !== null) {
        suppressed.push({ value: "pureid_qr", reason: pureidQRReason });
      } else {
        suppressed.push({ value: opt.value, reason: "other" });
      }
    }
    // The mobile-suppressed pureid_qr never enters the array;
    // synthesize its impression row so dashboards see it.
    if (
      mode === "enrollment" &&
      pureidQRReason === "mobile_browser"
       
    ) {
      suppressed.push({ value: "pureid_qr", reason: "mobile_browser" });
    }
    onPickerImpression({ shown, suppressed, mode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // I9 (2026-05-25): emit a developer-visible warning when the
  // picker is mounted in enrollment mode with the policy + UA
  // both saying "Card 2 should be visible" but the consumer
  // forgot to wire onUseQrEnroll. Silent suppression is the
  // class of bug this whole work-stream exists to close.
  // Phase 4 — ALSO fires the onPickerMisconfigured telemetry
  // hook so production deployments get a non-console signal.
  // Effect-deps deliberately exclude `mobile` (a function call,
  // not state) — the effect fires once on mount and any
  // remount, which is the cadence the warning is designed for.
  React.useEffect(() => {
    if (mode === "enrollment") {
      reportMisconfiguredEnrollmentPicker(
        qrChannelLive,
        mobile,
        consumerCanRoute,
      );
      if (
        onPickerMisconfigured &&
        qrChannelLive &&
        !mobile &&
        !consumerCanRoute
      ) {
        onPickerMisconfigured();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, qrChannelLive, consumerCanRoute]);
  // F5 — enrollment plan carries both the visible card list AND
  // the reason the pureid-QR card was suppressed (if any), so
  // reasonFor() can render distinguishable disabled-card copy
  // per the strategy doc §9 acceptance criterion.
  const enrollmentPlan =
    mode === "enrollment"
      ? enrollmentOptions(
          qrChannelLive,
          mobile,
          consumerCanRoute,
          ua,
          allowAnyOption,
        )
      : null;
  const rawOptions =
    mode === "enrollment" && enrollmentPlan !== null
      ? enrollmentPlan.options
      : signinOptions(ua);
  const pureidQRReason: PureIDQRSuppressionReason | null =
    enrollmentPlan?.pureidQRReason ?? null;
  // P6 — apply the per-tenant microcopy title override to the
  // pureid_qr card if one was supplied. Empty / whitespace-only
  // values fall through to the canonical "Save on my phone"
  // default (the title in PUREID_QR_OPTION).
  const titledOptions = pureidQRCardTitleOverride?.trim()
    ? rawOptions.map((opt) =>
        opt.value === "pureid_qr"
          ? { ...opt, title: pureidQRCardTitleOverride.trim() }
          : opt,
      )
    : rawOptions;
  // The tenant's allowlist, applied LAST so it governs the final
  // rendered set — every gate above (mode, QR channel, mobile,
  // consumer wiring) decides what COULD appear; this decides what
  // the tenant offers. Sign-in mode is untouched: see allowedCards.
  //
  // Everything downstream reads effectiveOptions — the dead-end
  // screen, the single-card collapse, the impression telemetry — so
  // filtering here means a tenant that narrows the catalogue to one
  // card gets the single-CTA layout, and one that narrows it to
  // nothing capable gets NoWorkingMethodScreen, both for free.
  const effectiveOptions =
    mode === "enrollment" && allowedCards !== undefined
      ? titledOptions.filter((opt) =>
          allowedCards.includes(allowlistMemberFor(opt.value)),
        )
      : titledOptions;
  // Device-level platform authenticator probe.
  //
  // null = probe hasn't completed yet (initial render before useEffect
  // fires, SSR, missing API). On null we render the "Use this device"
  // card as ENABLED — better to offer a working option that turns out
  // to fail than to disable a working option because the probe was
  // slow. The probe normally completes in ~1ms after mount on every
  // modern browser, and the result is memoised at module scope, so
  // subsequent picker mounts skip the probe entirely. The
  // null→false→re-render flicker is therefore visible only on the
  // first picker mount of a session, and only on devices where the
  // probe takes long enough for React to commit a frame between
  // mount and probe-resolved — which is rare enough that adding a
  // skeleton state to mask it would cost more UI complexity than the
  // ~1ms gap is worth.
  //
  // false = OS reports no UV-capable platform authenticator (no
  // Windows Hello PIN configured, no Touch ID enrolled, no Android
  // biometric, etc.). We disable "Use this device" with a config-
  // pointing message instead of letting the user click through to a
  // confused security-key prompt fallback. This was the canonical
  // "I clicked This Device and got the same error" failure mode.
  const [platformAvailable, setPlatformAvailable] = React.useState<boolean | null>(null);
  // Hybrid feasibility — see device-capabilities.ts for the layered
  // probe. Same null-permissive policy as platformAvailable: we'd
  // rather render the QR option enabled on browsers we can't probe
  // (Safari, Firefox) than hide a working option, since the
  // post-failure classifier and recovery surfaces will catch a
  // genuine fail. The probe DOES return high-confidence false on
  // Chromium when the OS reports no Bluetooth radio, and on any
  // browser after two confirmed phone-path fast-fails — that's the
  // Azure-VM dead-end this whole subsystem is here to prevent.
  const [hybridAvailable, setHybridAvailable] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    probePlatformAuthenticator().then((avail) => {
      if (!cancelled) setPlatformAvailable(avail);
    });
    probeHybridFeasibility().then((avail) => {
      if (!cancelled) setHybridAvailable(avail);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // deviceCapable returns false ONLY for an option whose underlying
  // transport is provably unavailable for this user on this device,
  // factoring BOTH the device probes AND the tenant's attachment
  // policy.
  //
  //   - this_device      → platform attachment. Disabled when
  //                        platform probe is high-confidence false
  //                        OR tenant policy is "cross-platform"
  //                        (excludes platform).
  //   - other_device     → cross-platform attachment via hybrid
  //                        (phone-QR). Disabled when hybrid probe
  //                        is high-confidence false OR tenant
  //                        policy is "platform" (excludes
  //                        cross-platform).
  //   - security_key     → cross-platform attachment via roaming
  //                        authenticator. No device-side probe
  //                        (we can't predict whether the user will
  //                        plug a key in), but the SAME tenant
  //                        policy gate applies — "platform" only
  //                        means security keys are disallowed too.
  //
  // The tenant gate is what enables the empty-intersection check
  // (OPTIONS.every below) to fire correctly at enrollment time.
  // Pre-Item-5: tenantAttachmentPolicy is undefined and treated
  // as "any" — every tenant gate evaluates to "allowed" and the
  // existing UX is preserved.
  const policy = tenantAttachmentPolicy ?? "";
  const policyAllowsPlatform = policy === "" || policy === "any" || policy === "platform";
  const policyAllowsCrossPlatform =
    policy === "" || policy === "any" || policy === "cross-platform";
  // Phone-approval policy: admin-controlled kill-switch for
  // the WebAuthn hybrid (caBLE) transport. Independent of
  // attachment_policy — a tenant can allow cross-platform
  // credentials (security keys + phones) while still
  // disabling the OS picker's Bluetooth-based phone path.
  // The `other_device` card is the only thing this disables;
  // `security_key` (the other cross-platform transport)
  // remains usable.
  const hybridChannelAllowed = tenantWebAuthnHybridDisabled !== true;
  const deviceCapable = (t: EnrollmentChoice): boolean => {
    if (t === "this_device") {
      return platformAvailable !== false && policyAllowsPlatform;
    }
    if (t === "other_device") {
      return (
        hybridAvailable !== false &&
        policyAllowsCrossPlatform &&
        hybridChannelAllowed
      );
    }
    if (t === "security_key") {
      return policyAllowsCrossPlatform;
    }
    if (t === "pureid_qr") {
      // 2026-05-25 (F5) — capability now reflects suppression
      // reasons. When pureidQRReason is non-null, the card is
      // rendered DISABLED with reason-specific copy. Mobile
      // is the one reason that still drops the card entirely
      // (mobile-on-mobile is a category error; rendering a
      // disabled "save on a phone" card to a phone user reads
      // as broken). For tenant_policy_disabled and
      // consumer_not_wired we keep the card visible-but-
      // disabled so the user sees the explanation.
      return pureidQRReason === null;
    }
    if (t === "any") {
      // ALWAYS capable, and that is the point. This card requests
      // no particular authenticator, so there is nothing for a
      // probe to contradict — and it is the escape hatch for
      // exactly the case where a probe is WRONG (a Chromium build
      // that reports no platform authenticator, a tenant-allowed
      // key this account has not registered yet). Disabling it on
      // capability grounds would remove the one card that does not
      // depend on capability. Tenant policy still binds: the
      // server applies the tenant's own authenticatorSelection,
      // because no hint means "honor the configuration".
      return true;
    }
    return true;
  };

  // Single availability predicate over an EnrollmentChoice. Only a
  // REAL transport has credential-side availability — the
  // stored-credential check asks "does this account hold a
  // credential of this kind", which is meaningless for the two
  // non-transport picks: `pureid_qr` is a flow channel (the phone
  // runs the ceremony) and `any` names no kind at all. Both are
  // therefore gated by deviceCapable alone, which is where their
  // real gates live (pureid_qr's suppression reasons; `any`'s
  // deliberate always-true). Routing all four availability call
  // sites through here keeps that split in ONE place.
  const choiceAvailable = (c: EnrollmentChoice): boolean => {
    if (!isPasskeyTransport(c)) {
      // 2026-05-25 (F5) — pureid_qr availability reflects the
      // suppression-reason state, not a hardcoded TRUE. When
      // suppressed by policy / consumer-not-wired, the card
      // renders disabled with reason copy (deviceCapable returns
      // false). When upstream gates pass, the card is selectable.
      return deviceCapable(c);
    }
    return isTransportAvailable(c, availableTransports) && deviceCapable(c);
  };

  // Reason copy for a disabled "this_device" card depends on WHY it's
  // disabled. We override the default "no enrolled passkey" reason
  // with a config-pointing one when the device-capability probe
  // failed. The copy is deliberately device-agnostic — Windows Hello,
  // Touch ID, and Android biometric all map to the same SP-side
  // outcome (the OS has no UV-capable platform authenticator), and
  // listing one platform's setting names confuses users on the
  // others. Generic "device sign-in settings" + parenthetical
  // examples covers every platform the WebAuthn-spec-compliant world
  // ships on.
  // Enrollment-aware unavailable copy. The original copy was
  // sign-in flavoured ("...for sign-in", "Sign in via your
  // phone"), which reads as a category error on the enrollment
  // surface where the user is CREATING their first credential.
  // We branch on `mode` so the same picker primitive serves
  // both surfaces with copy that matches the user's actual
  // task.
  const isEnrollment = mode === "enrollment";
  const reasonFor = (option: TransportOption): string => {
    // 2026-05-25 (F5) — pureid-QR suppression reasons. The card
    // is rendered disabled with these reason-specific copies
    // so the user understands WHY phone enrollment isn't
    // available rather than seeing two cards and assuming the
    // product only has two methods. Strategy doc §9 acceptance
    // criterion.
    if (option.value === "pureid_qr" && pureidQRReason !== null) {
      switch (pureidQRReason) {
        case "tenant_policy_disabled":
          return isEnrollment
            ? "Phone setup isn't enabled for your organization. Save your passkey on this device or use a security key instead, or contact your administrator."
            : "Phone setup isn't enabled for your organization.";
        case "consumer_not_wired":
          // I9 — programmer-visible misconfiguration. End-user
          // copy is deliberately generic; the dev-time
          // console.error carries the actionable detail.
          return "Phone setup isn't available on this surface. Save your passkey on this device or use a security key.";
        case "mobile_browser":
          // Note: this case shouldn't actually render disabled
          // (the card is omitted on mobile). Included for
          // exhaustiveness of the switch.
          return "You're on your phone — finish setup here.";
      }
    }
    // Tenant-policy exclusions take priority over device-side
    // reasons because the user can't fix policy themselves —
    // the message must direct them to admin contact, not to
    // OS-level setup.
    if (option.value === "this_device" && !policyAllowsPlatform) {
      return isEnrollment
        ? "Your organization doesn't allow built-in device passkeys (Touch ID, Hello, Android biometric) for new enrollments. Set up your passkey on your phone or a security key instead."
        : "Your tenant's policy doesn't allow built-in device passkeys (Windows Hello / Touch ID / Android biometric). Use your phone via QR or a security key, or contact your administrator.";
    }
    if (option.value === "other_device" && !policyAllowsCrossPlatform) {
      return isEnrollment
        ? "Your organization doesn't allow phone-based passkeys for new enrollments. Set up your passkey on this device or a security key instead."
        : "Your tenant's policy doesn't allow phone-based passkeys. Use this device's built-in unlock or contact your administrator.";
    }
    // Hybrid (caBLE) kill-switch takes priority over the
    // device-side hybrid probe reason — if an admin has
    // explicitly disabled the Bluetooth channel, the user
    // needs to know it's a policy decision (which they
    // CAN'T fix) rather than a device problem (which they
    // might try to "fix" by toggling Bluetooth).
    //
    // The "use QR sign-in instead" pointer fires only when
    // qrChannelLive — without that signal the SPA used to
    // hedge ("if your admin has enabled QR sign-in...") which
    // read as product uncertainty. Now we know.
    if (option.value === "other_device" && !hybridChannelAllowed) {
      if (qrChannelLive) {
        return isEnrollment
          ? "Your organization has turned off the Bluetooth-based phone passkey option. Set up your passkey on this device, on a security key, or open this enrollment link on a phone you can use here directly."
          : "Your organization has turned off the Bluetooth-based phone passkey option. Sign in with this device, a security key, or scan a QR with an already-enrolled phone to approve from there.";
      }
      return isEnrollment
        ? "Your organization has turned off the Bluetooth-based phone passkey option and cross-device QR sign-in. Set up your passkey on this device or on a security key."
        : "Your organization has turned off the Bluetooth-based phone passkey option and cross-device QR sign-in. Sign in with this device or a security key, or contact your administrator.";
    }
    if (option.value === "security_key" && !policyAllowsCrossPlatform) {
      return isEnrollment
        ? "Your organization doesn't allow hardware security keys for new enrollments. Set up your passkey on this device or your phone instead."
        : "Your tenant's policy doesn't allow security keys. Use this device's built-in unlock or contact your administrator.";
    }
    if (option.value === "this_device" && platformAvailable === false) {
      return isEnrollment
        ? "This device doesn't have Touch ID, Face ID, Windows Hello, or a screen lock turned on. Enable one in your device's settings (it takes ~30 seconds), then we can save your passkey here. Or pick another option below to set up elsewhere."
        : "This device doesn't have a configured biometric or PIN for sign-in. Set one up in your device's sign-in settings (e.g. Windows Hello, macOS Touch ID, Android biometric, ChromeOS PIN), then try again.";
    }
    if (option.value === "other_device" && hybridAvailable === false) {
      // The Azure-VM / Citrix / AVD scenario. Bluetooth is the
      // load-bearing transport for caBLE proximity verification —
      // without a radio (or with one that's been observed to fail
      // pairing twice), Chrome's "your device can't be used"
      // appears regardless of QR code presence. Tell the user the
      // truth about why: it's not a phone problem, it's a radio
      // problem on THIS device. If they have a different device
      // with Bluetooth they CAN use that — name the option.
      return isEnrollment
        ? "This device can't pair with a phone over Bluetooth (no radio, or pairing has failed here). Set up your passkey on this device's built-in unlock, on a hardware security key, or open this enrollment link on a different device with Bluetooth on."
        : "This device can't reach a phone for QR sign-in (Bluetooth isn't available, or pairing has repeatedly failed here). Use a different sign-in method, or sign in from a device with Bluetooth on.";
    }
    return option.unavailableReason;
  };

  // Trade-off note on the selected `value` becoming non-capable
  // post-probe: if the user lands with `value === "this_device"`
  // (e.g. saved preference from a Hello-capable device) and the new
  // device's probe returns false, the picker keeps `value` as-is
  // and the Continue button stays disabled. We do NOT auto-flip a
  // RENDERED card that turns out to be non-capable, because:
  //
  //   - the picker is fully controlled (consumer holds value), and
  //     consumers may persist `onChange` arguments to localStorage
  //     as the user's preference; an auto-flip would silently
  //     overwrite the user's saved preference with whatever the
  //     current device happens to support, surprising them next
  //     time they're back on a capable machine.
  //   - the disabled card's `reasonFor` text is large and prominent;
  //     the user reads it, picks a working card, and onChange fires
  //     with their explicit choice — which IS what should land in
  //     localStorage.
  //
  // The two cases where the picker DOES call onChange on its own are
  // narrower and live in the nudge effect below: exactly one usable
  // card survives (there is no choice left to make), or `value` is
  // not a rendered selectable card at all (nothing is highlighted
  // and the surface would be inert). Consumers route both through
  // useCeremonyTransport.set, which marks the value as picked;
  // seeding with `{ enrollment: true }` keeps an other_device seed
  // from ever needing the second nudge.
  //
  // The Continue button stays disabled in this case because of the
  // selectedAvailable check below. The user can never click through
  // to a guaranteed-failing ceremony.
  const selectedAvailable = choiceAvailable(value);

  // Pre-flight: if every option is unavailable, render the
  // dedicated NoWorkingMethodScreen instead of a picker that
  // shows three disabled cards and a greyed Continue button.
  // Three sub-cases drive different copy:
  //
  //   1. Tenant policy explicitly excludes the only methods
  //      this device CAN do → policy_excludes_capable. The
  //      user's environment is fine; the admin's restriction
  //      is what's blocking. Setup link wouldn't help.
  //
  //   2. Device CAN'T do the methods that ARE allowed →
  //      no_platform_no_hybrid (sign-in) or device_cannot_enroll
  //      (enrollment). The Azure-VM canonical case. Setup link
  //      and re-probe are the path forward.
  //
  //   3. Sign-in-time, the user simply has no credentials of
  //      a type their device can use →
  //      no_credentials_no_enrollment. Recovery is the only
  //      sensible path.
  //
  // The classification favours the policy-side cause when in
  // doubt — admins reading audit logs care about "the user hit
  // a policy wall" more than "the user's device was limited."
  //
  // Skipped at registration time when availableTransports is
  // empty AND the device has at least one viable transport;
  // there's no enrolled credential set to intersect with.
  const usableOptions = effectiveOptions.filter((o) => choiceAvailable(o.value));
  const probesResolved = platformAvailable !== null && hybridAvailable !== null;
  // P0.12 — when exactly one option is actually usable on this
  // device, auto-select it so the single-CTA layout's Continue
  // fires the right transport. Lifted out of render (it used to be
  // an `onChange()` call DURING render) into a post-commit effect:
  // a parent state update belongs in an effect, not the render
  // body. The effect is a no-op until the probes resolve and a
  // single option survives.
  //
  // The tenant allowlist adds a SECOND reason to nudge, and it is
  // not a refinement of the first: the selected card may not be
  // rendered at all. `value` is seeded from localStorage → server
  // preference → recommender → capability default, none of which
  // know what this tenant offers — so a returning user whose
  // remembered pick was since curated away arrives with a
  // selection pointing at a card that is not on screen. Nothing
  // looks selected, Continue is disabled by selectedAvailable, and
  // the picker is inert with no explanation. Before the allowlist
  // was applied this was unreachable, because every value was
  // always in the set.
  //
  // Both candidate lists exclude the action card. Nudging onto
  // `pureid_qr` sets a selection every consumer ignores, so the
  // picker would still show nothing selected and Continue would
  // still act on the stale pick — the same inert surface, now with
  // a wrong value behind it. (Observed in test the first time this
  // ran against an allowlist of {security_key}: the rendered set was
  // [phone, security key] and the nudge picked the phone.)
  const selectableUsable = usableOptions.filter((o) =>
    isSelectableCard(o.value),
  );
  const selectableRendered = effectiveOptions
    .filter((o) => isSelectableCard(o.value))
    .map((o) => o.value);
  const nudgeTo: EnrollmentChoice | null =
    probesResolved && usableOptions.length === 1
      ? // The single survivor may be the phone action card, which is
        // not a selection (see isSelectableCard); then there is
        // nothing to nudge onto and the user clicks it instead.
        (selectableUsable[0]?.value ?? null)
      : !selectableRendered.includes(value)
        ? // Prefer one this device can actually use; fall back to the
          // first rendered card while the probes are still in flight.
          // When nothing selectable survives we do not nudge — either
          // the dead-end screen renders, or the only card left is the
          // phone action card, which the user clicks rather than
          // selects.
          (selectableUsable[0]?.value ?? selectableRendered[0] ?? null)
        : null;
  React.useEffect(() => {
    if (nudgeTo !== null && value !== nudgeTo) {
      onChange(nudgeTo);
    }
  }, [nudgeTo, value, onChange]);

  const allOptionsUnavailable = effectiveOptions.every(
    (o) => !choiceAvailable(o.value),
  );
  if (allOptionsUnavailable) {
    const cause = classifyDeadEnd({
      mode,
      platformAvailable,
      hybridAvailable,
      availableTransports,
      tenantAttachmentPolicy,
      tenantWebAuthnHybridDisabled,
    });
    return (
      <NoWorkingMethodScreen
        cause={cause}
        mode={mode}
        ua={ua}
        onRetry={onRetry}
        onStartRecovery={onStartRecovery}
        supportEmail={supportEmail}
        tenantQrSigninEnabled={tenantQrSigninEnabled}
        onUseQrSignin={onUseQrSignin}
        onUseQrEnroll={onUseQrEnroll}
      />
    );
  }

  // P0.12 — hide-when-one-card: when exactly one option is
  // actually usable on this device, the multi-card picker implies
  // a choice that doesn't exist. Render a single "Continue with X"
  // CTA instead of three cards with two greyed out. The parent's
  // `value` is nudged to `only.value` by the post-commit effect
  // above (not during render). Only fires once the probes resolve.
  if (probesResolved && usableOptions.length === 1) {
    const only = usableOptions[0];
    // Suppress the multi-card chrome; render a single CTA.
    const isEnroll = mode === "enrollment";
    // Mental-model illustration (visual-system contract §4.2):
    // when this is enrollment, show the "where your passkey will
    // live" animation above the CTA. Only one path means we can
    // be precise about the destination — no ambiguity.
    const livesFamily =
      isEnroll && only && isPasskeyTransport(only.value)
        ? authenticatorFamilyFor(only.value, ua)
        : null;
    return (
      <div className="space-y-5">
        <div className="text-center space-y-1">
          <div className="mx-auto mb-3 h-12 w-12 rounded-xl border border-primary/30 bg-primary/10 flex items-center justify-center text-primary">
            {only?.icon ?? <KeyRound className="h-6 w-6" />}
          </div>
          <h2 className="text-base font-semibold">
            {isEnroll ? "Create your passkey" : "Sign in"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {only?.subtitle ?? subtitle}
          </p>
        </div>
        {livesFamily && (
          <div className="flex justify-center">
            <PasskeyLivesAnimation authenticator={livesFamily} width={260} />
          </div>
        )}
        <Button
          type="button"
          className="w-full"
          onClick={() => only && onContinue(only.value)}
          disabled={disabled}
        >
          {/* Titles are action phrases ("Use this device" / "Use this
              Windows PC"), so the button is the title itself — no
              verb prefix (which would double up: "Sign in with use…"). */}
          {only?.title ?? submitLabel}
        </Button>
        {showAccountSwitch && onChangeAccount ? (
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onChangeAccount}
          >
            Use a different account
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center space-y-0.5">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>

      {/* Optional diagnostic banner — names the situation before
          the user reads the cards. Used by sign-in surfaces to
          turn the picker from a free-form chooser into a triage
          menu ("we know your passkey isn't local; here's the
          shortest path"). Skipped when the consumer omits it
          (enrollment-time picker, /login/help page). */}
      {diagnostic ? (
        <div
          role="status"
          className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5 flex gap-2.5 text-xs leading-relaxed text-foreground/85"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/70" aria-hidden />
          <div className="flex-1">{diagnostic}</div>
        </div>
      ) : null}

      {/* "What's a passkey?" — collapsed disclosure in both modes.
        The concept is one click away for the first-time user
          without spending vertical space on the default view.
          Per the 2026-05-21 UX pass, the inline-in-enrollment
          treatment was retired as clutter on the highest-stakes
          screen — the per-card "When should I pick this?" rows
          carry the decision support now. */}
      <details className="group rounded-lg border border-border/40 bg-muted/10 px-3 py-2">
        <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground list-none flex items-center gap-1.5">
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          What&rsquo;s a passkey?
        </summary>
        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
          A passkey replaces your password with a cryptographic key your
          device holds locally. Sign-in is one tap with your fingerprint,
          face, or device PIN — and it can&rsquo;t be phished, because the
          key never leaves your device.
        </p>
      </details>

      {/* The pre-selected card IS the recommendation — no pill.
          Each card carries a collapsed "When should I pick this?"
          disclosure with the decision-support guidance. */}
      <div className="space-y-2" role="radiogroup" aria-label="Passkey option">
        {effectiveOptions.map((opt) => {
          // A card is enabled only when both the credential-side
          // check (does the user have a passkey of this transport?)
          // AND the device-side check (can this browser actually
          // produce/consume one?) pass. Either failing disables the
          // card. The reason copy reflects whichever check failed
          // first via reasonFor().
          const available = choiceAvailable(opt.value);
          // When the caller supplies a per-card preview copy
          // for this transport, swap it in for the option's
          // default subtitle on AVAILABLE cards. Disabled cards
          // still surface their unavailableReason — the user
          // needs to know why they can't pick this option, not
          // what would have happened if they could.
          // cardPreviewCopy is keyed by real transport only; the
          // non-transport cards never carry preview copy (their
          // subtitles are fixed — there is no "what happens next"
          // to preview when the next screen is the one making the
          // choice). Guard the index access so the EnrollmentChoice
          // can't widen the lookup type.
          const previewSubtitle = isPasskeyTransport(opt.value)
            ? cardPreviewCopy?.[opt.value]
            : undefined;
          const optWithPreview =
            previewSubtitle && available
              ? { ...opt, subtitle: previewSubtitle }
              : opt;
          return (
            <TransportCard
              key={opt.value}
              option={optWithPreview}
              active={value === opt.value}
              available={available}
              unavailableReason={reasonFor(opt)}
              disabled={disabled}
              onSelect={() => {
                // P5 — fire the analytics hook BEFORE onChange so
                // a consumer that derives state from onChange
                // sees the click event ordered correctly.
                if (onCardClicked) onCardClicked(opt.value);
                // pureid_qr is an ACTION card (a flow-channel), not a
                // stored-transport selection: the controlled `value`
                // is PasskeyTransport-typed and cannot hold it, so
                // onChange("pureid_qr") is a deliberate no-op at every
                // consumer — which left the card un-selectable on
                // click. Route straight to the QR-enrollment surface
                // via its dedicated handler instead. When this card
                // renders enabled, consumerCanRoute (effectiveOptions
                // gating) guarantees onUseQrEnroll is defined.
                if (opt.value === "pureid_qr") {
                  onUseQrEnroll?.();
                  return;
                }
                onChange(opt.value);
              }}
            />
          );
        })}
      </div>

      <Button
        type="button"
        className="w-full"
        disabled={!!disabled || !selectedAvailable}
        onClick={() => onContinue(value)}
      >
        {submitLabel}
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>

      {(showAccountSwitch || showRecovery) && (
        <div className="pt-3 mt-1 border-t border-border/40 space-y-3">
          {showAccountSwitch && onChangeAccount && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              onClick={onChangeAccount}
              disabled={disabled}
            >
              <RotateCcw className="h-3 w-3" />
              Use a different account
            </button>
          )}
          {showRecovery && onStartRecovery && (
            <AccountRecoveryOption onRecover={onStartRecovery} disabled={disabled} />
          )}
        </div>
      )}
    </div>
  );
}

interface TransportCardProps {
  option: TransportOption;
  active: boolean;
  available: boolean;
  /** Subtitle shown when the card is disabled. Caller picks the
   *  appropriate copy — credential-based ("no passkey for this
   *  browser") vs device-capability-based ("Windows Hello not set up")
   *  reasons surface different actionable instructions. */
  unavailableReason: string;
  disabled?: boolean;
  /** Selects this option. Named onSelect (not onClick) because
   *  the card is a role=radio div, not a <button> — it nests an
   *  interactive "When should I pick this?" disclosure, which a
   *  <button> can't legally contain. */
  onSelect: () => void;
}

/**
 * UX-1 (2.2): pick the single transport to flag as
 * "Recommended" so first-time users see a guided default.
 *
 * Heuristic:
 *
 *   1. If platform authenticator is confirmed available
 *      AND `this_device` is in the user's credential set
 *      (or there are no credentials yet — enrollment
 *      time), recommend "this_device" — fastest happy
 *      path on a desktop with Hello / Touch ID.
 *
 *   2. Otherwise, if any "other_device" credential exists
 *      OR there are no credentials yet, recommend
 *      "other_device" — works on any device with a phone
 *      handy, the broadest fallback.
 *
 *   3. Otherwise return null (no recommendation; the user
 *      sees three equal-weight cards as before).
 *
 * The recommendation is advisory only; the picker still
 * lets the user pick any enabled card.
 */
/**
 * Classify the empty-intersection case so NoWorkingMethodScreen
 * can render the right copy.
 *
 * Resolution order is INTENTIONAL:
 *
 *   1. Policy excludes everything the device CAN do — most
 *      operationally important to surface, since the admin
 *      can fix it (and the user can't).
 *   2. Device can do nothing the policy allows — Azure-VM
 *      canonical case; setup link applies.
 *   3. Sign-in-time, user has no credentials of any type
 *      their device can produce — recovery flow is the
 *      answer.
 *
 * Returns a single value from NoWorkingMethodCause; never
 * returns undefined.
 */
interface DeadEndContext {
  mode: "signin" | "enrollment";
  platformAvailable: boolean | null;
  hybridAvailable: boolean | null;
  availableTransports: readonly string[] | undefined;
  tenantAttachmentPolicy: string | undefined;
  tenantWebAuthnHybridDisabled: boolean | undefined;
}

function classifyDeadEnd(ctx: DeadEndContext): NoWorkingMethodCause {
  // Empty / "any" → no policy restriction; both map to the
  // same on-the-wire effect (every attachment allowed). The
  // policy_excludes_capable branch is suppressed for these
  // values regardless of device state.
  const policy = ctx.tenantAttachmentPolicy ?? "";
  const policyAllowsPlatform = policy === "" || policy === "any" || policy === "platform";
  const policyAllowsCrossPlatform =
    policy === "" || policy === "any" || policy === "cross-platform";
  // Hybrid-channel kill-switch composes with the attachment
  // policy: a tenant that allows cross-platform credentials
  // but disables the OS picker's caBLE path effectively
  // disables `other_device` for everyone. Folded into
  // policyAllowsCrossDeviceHybrid so the cross-channel-
  // exclusion classification fires uniformly whether the
  // gate is attachment_policy=platform or hybrid_disabled=true.
  const hybridChannelAllowed = ctx.tenantWebAuthnHybridDisabled !== true;
  const policyAllowsCrossDeviceHybrid =
    policyAllowsCrossPlatform && hybridChannelAllowed;

  // Tri-valued contract reminder:
  //   - true  → device can do this transport (high confidence)
  //   - false → device can't do this transport (high confidence)
  //   - null  → couldn't determine (browser doesn't expose API)
  //
  // POLICY-EXCLUDES classification REQUIRES high-confidence
  // capability. We fire it only when we KNOW the device can
  // do something the policy excludes — null doesn't qualify.
  // The previous logic treated null as permissive ("device
  // can hybrid"), which mis-classified Linux+Firefox users
  // (no Bluetooth API, no Hello configured, tenant=platform)
  // as policy_excludes_capable when the actual cause was
  // "device can't do anything we know of."
  const deviceDefinitelyCanPlatform = ctx.platformAvailable === true;
  const deviceDefinitelyCanHybrid = ctx.hybridAvailable === true;

  // Cause #1: high-confidence "device CAN do X, but policy
  // forbids X" — and no other (capability, policy) combination
  // resolves to a working pair. Forbidden-AND-stuck is what
  // distinguishes "policy excludes the only thing you can do"
  // from "you happen to also be able to do something allowed."
  const platformCombo = policyAllowsPlatform && deviceDefinitelyCanPlatform;
  const hybridCombo = policyAllowsCrossDeviceHybrid && deviceDefinitelyCanHybrid;
  const policyBlocksKnownCapability =
    (deviceDefinitelyCanPlatform && !policyAllowsPlatform) ||
    (deviceDefinitelyCanHybrid && !policyAllowsCrossDeviceHybrid);
  if (policyBlocksKnownCapability && !platformCombo && !hybridCombo) {
    return "policy_excludes_capable";
  }

  // Sign-in mode + the user has enrolled credentials → if the
  // device can't satisfy ANY of them, the honest cause is
  // "your passkey is on another device", not "set up Hello
  // here." Pre-empts cause #2 (no_platform_no_hybrid) for the
  // returning-user case so the setup-link CTA — which is the
  // misleading advice we're killing — doesn't surface.
  //
  // "User has enrolled credentials" = availableTransports
  // non-empty. Empty array means "no credentials yet" (fresh
  // user), where setup-link IS the right next step.
  if (
    ctx.mode === "signin" &&
    ctx.availableTransports !== undefined &&
    ctx.availableTransports.length > 0 &&
    ctx.platformAvailable !== true &&
    ctx.hybridAvailable !== true
  ) {
    return "credentials_on_other_device";
  }

  // Cause #2: device can't produce anything we can verify;
  // setup link applies because enabling Windows Hello / Touch
  // ID is the most likely fix. Enrollment-mode gets the
  // slightly-different copy variant. We hit this when both
  // probes returned high-confidence false OR when Layer 3
  // returned false on one and Layer 1+2 returned null on the
  // other (most-conservative "we can't see anything that
  // works" interpretation). The pre-empt above ensures known
  // users get the more honest cause first.
  if (ctx.platformAvailable !== true && ctx.hybridAvailable !== true) {
    return ctx.mode === "enrollment" ? "device_cannot_enroll" : "no_platform_no_hybrid";
  }

  // Cause #3: sign-in time only — device has at least one
  // viable transport, but the user's credentials don't include
  // anything that uses it. Recovery is the unstuck path.
  if (ctx.mode === "signin" && ctx.availableTransports !== undefined) {
    return "no_credentials_no_enrollment";
  }

  // Fallback: the structural reason isn't determinable.
  // device_cannot_enroll is the safest default — it foregrounds
  // the setup link AND the recovery option, so the user has
  // something to try regardless.
  return ctx.mode === "enrollment" ? "device_cannot_enroll" : "no_platform_no_hybrid";
}

function TransportCard({
  option,
  active,
  available,
  unavailableReason,
  disabled,
  onSelect,
}: TransportCardProps) {
  const cardDisabled = !!disabled || !available;
  // role=radio div (not a <button>) so the per-card "When should
  // I pick this?" <details> can nest legally. Keyboard parity
  // with a native radio: Enter/Space select; the disclosure's
  // own summary handles its own keys and stops propagation so a
  // Space on it toggles details rather than selecting the card.
  return (
    <div
      role="radio"
      aria-checked={active}
      aria-disabled={cardDisabled}
      tabIndex={cardDisabled ? -1 : 0}
      onClick={cardDisabled ? undefined : onSelect}
      onKeyDown={
        cardDisabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect();
              }
            }
      }
      className={cn(
        "w-full text-left rounded-xl border p-3 transition-colors outline-none",
        cardDisabled
          ? "opacity-60 cursor-not-allowed border-border/40 bg-muted/10"
          : active
            ? "border-primary bg-primary/5 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary"
            : "border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
            active && !cardDisabled
              ? "bg-primary/15 text-primary"
              : "bg-muted/60 text-muted-foreground",
          )}
        >
          {option.icon}
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-foreground">
            {option.title}
          </span>
          {/* The single decision block — always visible (no expand/
              collapse). Leads with WHEN to pick this option; a
              disabled card shows why it can't be used instead. */}
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {!available ? unavailableReason : option.subtitle}
          </p>
        </div>
        {/*
          Per-card line-art: the concrete thing each option does
          (phone+QR, laptop+biometric, USB key). Compact 72px so
          it anchors the card without dominating. Hidden on
          disabled cards.
        */}
        {!cardDisabled ? (
          <TransportIllustration
            transport={option.value}
            active={active}
            className="hidden sm:block w-[72px] shrink-0 self-center"
          />
        ) : null}
      </div>
    </div>
  );
}
